// DTOs de C9 — resumen semanal exportable.
//
// Vista compacta para presentar a directivos: 7 KPIs + comparativo vs semana
// anterior + top eventos + breakdown por planta.
//
// Endpoints:
//   GET /api/coordinador/resumen-semanal?semana=YYYY-MM-DD  (lunes de la semana)
//   GET /api/coordinador/resumen-semanal.csv?semana=...    (mismo dato en CSV)
import { z } from 'zod';
// =============================================================================
// Query
// =============================================================================
export const resumenSemanalQuerySchema = z.object({
    /** Lunes de la semana a resumir. Si se omite, usa la semana en curso. */
    semana: z.string().date().optional(),
});
// =============================================================================
// KPIs con comparativo
// =============================================================================
/** KPI numérico con valor de esta semana, semana anterior, delta absoluto y %. */
export const kpiComparativoSchema = z.object({
    actual: z.number(),
    anterior: z.number(),
    /** actual - anterior. */
    delta_abs: z.number(),
    /** (actual - anterior) / anterior * 100. null cuando anterior = 0. */
    delta_pct: z.number().nullable(),
});
export const resumenKpisSchema = z.object({
    cumplimiento_pct: kpiComparativoSchema,
    cantidad_real: kpiComparativoSchema,
    cantidad_programa: kpiComparativoSchema,
    /** B2: paro total registrado (incl. programados), en horas, solo eventos cerrados. */
    horas_paro: kpiComparativoSchema,
    /** B1: minutos de paros CERRADOS cuya categoría afecta disponibilidad. */
    paro_disponibilidad_min: kpiComparativoSchema,
    lineas_con_captura: kpiComparativoSchema,
    bloques_capturados: kpiComparativoSchema,
    acciones_supervision: kpiComparativoSchema,
});
// =============================================================================
// Top eventos
// =============================================================================
// Top líneas peor cumplimiento vs CAPACIDAD instalada (WS-4): el programa vive a
// nivel área, así que el cumplimiento per-línea solo es honesto contra la
// capacidad de la línea (no contra el programa, que sería doble conteo).
export const topLineaSchema = z.object({
    linea_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    planta_nombre: z.string(),
    /** Cumplimiento vs capacidad: real / esperado_capacidad * 100 (null si capacidad=0). */
    cumplimiento_meta_pct: z.number().nullable(),
    cantidad_real: z.number(),
    /** Capacidad instalada esperada de la línea en el período (Σ cap_hora × bloques). */
    esperado_capacidad: z.number(),
    /** Brecha vs capacidad: max(esperado_capacidad - real, 0). */
    unidades_sin_producir: z.number(),
});
export const topCausalSchema = z.object({
    causal_id: z.string().uuid(),
    causal_nombre: z.string(),
    causal_madre_nombre: z.string().nullable(),
    minutos_total: z.number(),
    horas_total: z.number(),
    paros_count: z.number().int().min(0),
});
// Por planta — datos agregados solo de esta semana (sin comparativo, simplifica)
export const resumenPlantaSchema = z.object({
    planta_id: z.string().uuid(),
    planta_codigo: z.string(),
    planta_nombre: z.string(),
    cumplimiento_pct: z.number().nullable(),
    cantidad_real: z.number(),
    cantidad_programa: z.number(),
    /** B2: paro total registrado (incl. programados), en horas, solo cerrados. */
    horas_paro: z.number(),
    /** B1: minutos de paros cerrados que afectan disponibilidad. */
    paro_disponibilidad_min: z.number().min(0),
    /** DN1: eventos abiertos de la planta — aparte, no suman. */
    paros_en_curso: z.number().int().min(0),
    lineas_con_captura: z.number().int().min(0),
    lineas_total: z.number().int().min(0),
});
/** A6: lo capturado que supervisión aún no validó, sobre la semana vista. */
export const resumenPendienteValidacionSchema = z.object({
    bloques: z.number().int().min(0),
    paquetes: z.number().min(0),
    /** paquetes pendientes / producido × 100. null si no hay producción. */
    pct: z.number().nullable(),
});
// =============================================================================
// Serie diaria — evolución de la semana (para gráfico)
// =============================================================================
/** Un punto por día con datos dentro de la semana vista (orden ASC por fecha). */
export const resumenSerieDiariaPuntoSchema = z.object({
    /** Día del punto (YYYY-MM-DD). */
    fecha: z.string().date(),
    /** Cumplimiento vs meta de capacidad de ese día. null si no hay meta. */
    cumplimiento_meta_pct: z.number().nullable(),
    /** Unidades reales producidas en el día. */
    cantidad_real: z.number().min(0),
    /** Horas de paro acumuladas en el día. */
    horas_paro: z.number().min(0),
});
// =============================================================================
// Rango de la semana (DN8: comparación a la misma altura)
// =============================================================================
export const resumenSemanaRangoSchema = z.object({
    /** Inicio de la semana operativa vista. */
    desde: z.string().date(),
    /** Fin de la semana vista. En la semana EN CURSO es el día operativo actual. */
    hasta: z.string().date(),
    /** Inicio de la semana anterior (desde - 7). */
    desde_anterior: z.string().date(),
    /** Misma altura que `hasta` en la semana anterior (hasta - 7). */
    hasta_anterior: z.string().date(),
    /** true cuando la semana vista está en curso y aún no está completa. */
    parcial: z.boolean(),
    /** Días incluidos en ambos rangos (1–7). */
    dias: z.number().int().min(1).max(7),
});
// =============================================================================
// Response
// =============================================================================
export const coordinadorResumenSemanalSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    semana: resumenSemanaRangoSchema,
    kpis: resumenKpisSchema,
    /** DN1: eventos de paro abiertos en la semana vista — aparte, no suman. */
    paros_en_curso: z.number().int().min(0),
    pendiente_validacion: resumenPendienteValidacionSchema,
    top_lineas_peor_cumplimiento: z.array(topLineaSchema),
    top_paros: z.array(topCausalSchema),
    por_planta: z.array(resumenPlantaSchema),
    /** Evolución día a día de la semana vista (para el gráfico). Orden ASC por fecha. */
    serie_diaria: z.array(resumenSerieDiariaPuntoSchema),
    /** Alertas importantes detectadas en la semana. */
    alertas: z.array(z.object({
        tipo: z.enum([
            'cumplimiento_critico',
            'paros_excesivos',
            'sin_captura',
            'invalidaciones_altas',
        ]),
        severidad: z.enum(['alta', 'media']),
        mensaje: z.string(),
    })),
});
//# sourceMappingURL=coordinador_resumen_semanal.js.map